import * as React from 'react';
import { ActivityFeedViewV2 } from './ActivityFeedView';
import { ActivityFeedItem } from '../service/ActivityFeedItem';
import { getActivityFeedStore } from '../store/store';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import loadActivityFeed from '../utils/loadActivityFeed';
import { activityFeedItemAction } from '../actions/activityFeedItemAction';
import {
    ActivityFeedActionType,
    OpenMailAction,
    OpenLinkAction,
    UpdateBadgeAction,
} from './ActivityFeedActions';
import { logUsage } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { ActivityType } from '../service/ActivityFeedStorageItem';
import { dismissAllItems } from '../actions/dismissAllItems';
import { toggleActivityFeed } from '../actions/toggleActivityFeed';
import ActivityFeedSettings from './ActivityFeedSettings';
import popoutCalendarReadingPane from 'owa-popout/lib/utils/popout/popoutCalendarReadingPane';
import makeCardActionServiceCall from '../service/makeCardActionServiceCall';
import saveActivityFeedSettings from '../utils/saveActivityFeedSettings';
import { pollActivityFeed } from '../utils/pollActivityFeed';
import { observer } from 'mobx-react-lite';

declare var outlook;
export interface ActivityFeedProps {
    openMailAction: OpenMailAction;
    openLinkAction: OpenLinkAction;
    updateBadgeAction?: UpdateBadgeAction;
    isOpx?: boolean;
}
type MetricSummary = { [key in ActivityType]?: number } & {
    sessionId: string;
    unseen: number;
    total: number;
};

export default observer(function ActivityFeed(props: ActivityFeedProps) {
    React.useEffect(() => {
        sessionId.current = getGuid();
        toggleActivityFeed(true);
        const items = [...getActivityFeedStore().items.values()];
        logUsage('ActivityFeed_Loaded', createSummary(items));
        loadActivityFeed();
        pollActivityFeed(props.isOpx, props.updateBadgeAction);
        return () => {
            toggleActivityFeed(false);
            saveActivityFeedSettings();
            logUsage('ActivityFeed_Unloaded', { sessionId: sessionId.current });
        };
    }, []);

    // session is defined as the span of time between this component is loaded and unloaded
    const sessionId = React.useRef<string>();
    const createSummary = (items: ActivityFeedItem[]): MetricSummary => {
        let summary: MetricSummary = {
            sessionId: sessionId.current,
            unseen: 0,
            total: items.length,
        };
        items.forEach(item => {
            const key = item.type;
            if (!(key in summary)) {
                summary[key] = 0;
            }
            if (!item.isSeen) {
                summary[key]++;
                summary.unseen++;
            }
        });
        return summary;
    };
    //Return settings if the activityFeedSettingsOpen flag is true
    if (getActivityFeedStore().isActivityFeedSettingOpen) {
        return <ActivityFeedSettings isOpx={props.isOpx} />;
    }
    const executeAction = (itemId: string, action: ActivityFeedActionType) => {
        // AdaptiveCard has a set of predefined action that we are "intercepting" and replacing with custom behaviors.
        // If we want to use one of the AdaptiveCard action we need to add a case here and define how to handle it.
        // If we want to implement a fully custom action use Action.OpenMail as example.
        switch (action.actionName) {
            case 'Action.OpenMail':
                props.openMailAction.openMail(action.internetMessageId, action.messageItemId);
                break;
            case 'Action.OpenUrl':
                props.openLinkAction.openLink(action.url);
                break;
            case 'Action.MakeApiCall':
                makeCardActionServiceCall(action.url, action.body, action.methodType);
                break;
            case 'Action.OpenCalendarEvent':
                popoutCalendarReadingPane(action.meetingItemId, 'ActivityFeed', action.data);
                break;
            default:
                break;
        }
    };

    const handleAction = (itemId: string, action: ActivityFeedActionType) => {
        // temp hack to not mark as read if action type is makeapicall
        if (action.actionName != 'Action.MakeApiCall') {
            activityFeedItemAction(itemId);
        }
        const item: ActivityFeedItem = getActivityFeedStore().items.get(itemId);
        let actionName: string = action.actionName;
        logUsage('ActivityFeed_ActionClicked', {
            sessionId: sessionId.current,
            action: actionName,
            actionNameExtraInformation: action.actionNameExtraInformation,
            itemId: itemId,
            itemType: item.type,
            itemRead: item.isRead,
            itemSeen: item.isSeen,
        });
        executeAction(itemId, action);
    };
    const { firstFetchComplete, items } = getActivityFeedStore();
    // Temporary hack to remove the loading indicator in activity feed opx
    const loadingIndicator = props.isOpx ? <div /> : <Spinner size={SpinnerSize.large} />;
    return firstFetchComplete ? (
        <ActivityFeedViewV2
            cards={[...items.values()]}
            onAction={handleAction}
            onDismissAll={dismissAllItems}
            isOpx={props.isOpx}
        />
    ) : (
        loadingIndicator
    );
});
